import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, BookOpen, ShieldCheck, TrendingUp } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { blogPosts } from "../../data/blogPosts";
import { blogCategories, getCategoryName } from "../../data/blogCategories";
import { business } from "../../config/business";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const faqs = [
  { q: "Are these guides specific to TR Structures' projects?", a: "They cover general PEB, industrial shed and construction industry knowledge, with Telangana/AP-specific context where relevant — not case studies of specific past projects." },
  { q: "How often is new content added?", a: "We add guides periodically as we identify useful topics — check back or explore by category for the latest." },
];

const BlogIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory = activeCategory === "all" || post.category === activeCategory;
      const matchesQuery =
        query.trim() === "" ||
        post.title.toLowerCase().includes(query.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(query.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, query]);

  const featured = blogPosts.slice(0, 3);

  return (
    <Layout>
      <SEOHead
        title="Resources & Guides | PEB, Industrial Sheds & Steel Fabrication — TR Structures"
        description="In-depth guides on PEB construction, industrial sheds, warehouses, steel fabrication and cost planning from TR Structures' engineering team."
        keywords="PEB guides, industrial shed guides, warehouse construction guides, steel fabrication guides, PEB cost India"
      />
      <Breadcrumb items={[{ name: "Resources" }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-8">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
            Resources & <span className="text-secondary">Guides</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl text-lg mb-8">
            Practical guides on PEB construction, industrial sheds, warehouses, steel fabrication and
            project cost planning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4 text-center">
              Talk to Our Engineering Team
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><BookOpen size={18} aria-hidden="true" /> 50 in-depth guides</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Sourced, factual content</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><TrendingUp size={18} aria-hidden="true" /> 6 categories covered</span>
          </div>
        </div>
      </section>

      {/* 3. Featured guides */}
      <section className="section-padding pt-14">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6">Featured Guides</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {featured.map((post) => (
              <Link
                key={post.slug}
                to={`/resources/${post.slug}`}
                className="p-6 bg-card rounded-xl shadow-md card-hover border border-border flex flex-col"
              >
                <p className="text-xs text-secondary font-semibold mb-2">{getCategoryName(post.category)}</p>
                <h3 className="font-montserrat font-bold text-foreground mb-2 flex-grow">{post.title}</h3>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock size={12} aria-hidden="true" /> {post.readTime}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Search + filter + grid */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <div className="mb-8">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides..."
              aria-label="Search resources"
              className="w-full max-w-md px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background"
            />
          </div>

          <div className="flex flex-wrap gap-3 mb-10">
            <button
              onClick={() => setSearchParams({})}
              className={`px-4 py-2 rounded-full font-montserrat font-semibold text-sm transition ${
                activeCategory === "all" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-primary/10"
              }`}
            >
              All
            </button>
            {blogCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSearchParams({ category: cat.id })}
                className={`px-4 py-2 rounded-full font-montserrat font-semibold text-sm transition ${
                  activeCategory === cat.id ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-primary/10"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No guides found. Try a different search or category.</p>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.05 }}
              transition={{ staggerChildren: 0.04 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filtered.map((post) => (
                <motion.div key={post.slug} variants={fadeUp}>
                  <Link
                    to={`/resources/${post.slug}`}
                    className="p-6 bg-card rounded-xl shadow-md card-hover border border-border flex flex-col h-full"
                  >
                    <p className="text-xs text-secondary font-semibold mb-2">{getCategoryName(post.category)}</p>
                    <h2 className="font-montserrat font-bold text-foreground mb-2 flex-grow">{post.title}</h2>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{post.excerpt}</p>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock size={12} aria-hidden="true" /> {post.readTime}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* 5. Categories overview */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6 text-center">Browse by Category</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {blogCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSearchParams({ category: cat.id })}
                className="p-5 bg-card rounded-xl shadow-md card-hover border border-border text-left"
              >
                <h3 className="font-montserrat font-bold text-foreground">{cat.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {blogPosts.filter((p) => p.category === cat.id).length} guides
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={faqs} />

      {/* 7. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Have a Project in Mind?
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Talk to our engineering team about your specific requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogIndex;
